import {
  faArrowDownToLine,
  faArrowsDownToPeople,
  faChartMixed,
  faGearComplexCode,
  faPeopleGroup,
} from "@fortawesome/pro-light-svg-icons";
import { Box, styled } from "@mui/joy";
import { useLocalStorageState } from "ahooks";
import { Fragment } from "react";
import { portalTypeStorageKeyName } from "features/dmp/constants";
import { PortalType } from "features/dmp/types";
import { useOrganizationPreferences, useUserRole } from "hooks";
import SidebarMenuItem from "../SidebarMenuItem/SidebarMenuItem";
import AdminPortalSidebarMenuItem from "./AdminPortalSidebarMenuItem/AdminPortalSidebarMenuItem";
import DatalabsSidebarMenuItem from "./DatalabsSidebarMenuItem/DatalabsSidebarMenuItem";
import DataPartnerPortalMenuItem from "./DataPartnerPortalMenuItem/DataPartnerPortalMenuItem";
import DataRoomsListSidebarMenuItem from "./DataRoomsListSidebarMenuItem/DataRoomsListSidebarMenuItem";
import DatasetsSidebarMenuItem from "./DatasetsSidebarMenuItem/DatasetsSidebarMenuItem";
import DocumentationSidebarMenuItem from "./DocumentationSidebarMenuItem/DocumentationSidebarMenuItem";
import NewDataRoomSidebarMenuItem from "./NewDataRoomSidebarMenuItem/NewDataRoomSidebarMenuItem";
import OrganizationLogoSidebarItem from "./OrganizationLogoSidebarItem/OrganizationLogoSidebarItem";
import PublisherPortalMenuItem from "./PublisherPortalMenuItem/PublisherPortalMenuItem";
import ToggleSidebarButton from "./ToggleSidebarButton/ToggleSidebarButton";

const ToggleSidebarButtonWrapper = styled(Box)(({ theme: { spacing } }) => ({
  alignItems: "center",
  backgroundColor: "var(--joy-palette-background-surface)",
  bottom: 0,
  display: "flex",
  flexDirection: "column",
  marginTop: "auto",
  padding: spacing(2, 0),
  // In order to make the area outside this box scrollable and this box attached to the bottom,
  // the components above in JSX should be wrapped into additional Box,
  // which would violate Joy's principles of nested components (List > ListItem) hence breaking the layout and animations completely
  // Considering all the above, this solution is not perfect but the easiest and working
  position: "sticky",
  width: "100%",
}));

const SidebarContent: React.FC = () => {
  const [portalType] = useLocalStorageState<string | undefined>(
    portalTypeStorageKeyName,
    { defaultValue: PortalType.DCR, listenStorageChange: true }
  );
  const {
    isSubscribedState,
    isSuspendedState,
    hasPublisherFeatures,
    hasDataPartnerFeatures,
    hasDatalabFeatures,
  } = useOrganizationPreferences();
  const { isSuperAdmin, isOrganizationAdmin, isSuperAdminReadOnly } =
    useUserRole();
  return portalType === PortalType.DCR ? (
    <Fragment>
      <NewDataRoomSidebarMenuItem />
      <DataRoomsListSidebarMenuItem />
      {hasDatalabFeatures &&
      !(hasPublisherFeatures || hasDataPartnerFeatures) ? (
        <DatalabsSidebarMenuItem />
      ) : null}
      {hasPublisherFeatures && <PublisherPortalMenuItem />}
      {hasDataPartnerFeatures && <DataPartnerPortalMenuItem />}
      <DatasetsSidebarMenuItem />
      {(isSuperAdmin || isOrganizationAdmin || isSuperAdminReadOnly) &&
        (isSubscribedState || isSuspendedState) && (
          <AdminPortalSidebarMenuItem />
        )}
      <DocumentationSidebarMenuItem />
      <ToggleSidebarButtonWrapper>
        <OrganizationLogoSidebarItem />
        <ToggleSidebarButton />
      </ToggleSidebarButtonWrapper>
    </Fragment>
  ) : portalType === PortalType.PAP ? (
    <Fragment>
      <SidebarMenuItem
        icon={faPeopleGroup}
        label="Audiences"
        to="/pap/audiences"
        tooltipTitle="Audiences"
      />
      <SidebarMenuItem
        icon={faChartMixed}
        label="Insights"
        to="/pap/insights"
        tooltipTitle="Insights"
      />
      <SidebarMenuItem
        icon={faArrowDownToLine}
        label="Exports"
        to="/pap/exports"
        tooltipTitle="Exports"
      />
      <SidebarMenuItem
        icon={faArrowsDownToPeople}
        label="Activation"
        to="/pap/activation"
        tooltipTitle="Activation"
      />
      <SidebarMenuItem
        icon={faGearComplexCode}
        label="Configuration"
        to="/pap/configuration"
        tooltipTitle="Configuration"
      />
      {(isSuperAdmin || isOrganizationAdmin || isSuperAdminReadOnly) &&
        (isSubscribedState || isSuspendedState) && (
          <AdminPortalSidebarMenuItem />
        )}
      <DocumentationSidebarMenuItem />
      <ToggleSidebarButtonWrapper>
        <OrganizationLogoSidebarItem />
        <ToggleSidebarButton />
      </ToggleSidebarButtonWrapper>
    </Fragment>
  ) : null;
};

export default SidebarContent;
